import React, { Component, createRef } from 'react'
import { createStructuredSelector } from 'reselect'
import { connect } from 'react-redux'
import styled from 'styled-components'
import onClickOutside from 'react-onclickoutside'
import { escapeRegExp, filter, concat, debounce } from 'lodash'

import { cn } from 'utils/classMerge'

import { mediaQueries, palette } from '../../styles/variables'
import { translate } from '../../utils/translation'
import {
  getLocationInfo,
  hasLanguage,
  generateLangLocOptions,
  generateNewLangLocOptions,
  generateCompetitorLangLocOptions,
  isLanguageBanned,
  isValidCompetitorLangLoc,
  isCity,
  isGlobal,
  getCachedLocationById,
} from '../../utils/locations'
import { LoadingResponsive } from '../loading/Loading'
import { generateKey } from '../../utils/functions'
import {
  getLocationsInfo,
  getLocationSuggestions,
  saveLocationInfo,
} from '../../state/actions/locationManager'
import {
  selectCachedLocations,
  selectIsLoadingSuggestions,
} from '../../state/selectors/locationManager'

import {
  LanguageSelectorWrapper as DefaultLanguageSelectorWrapper,
  SelectedCountry as DefaultSelectedCountry,
  CountryDropdown,
  Country,
  Language,
  Label,
  SearchInput,
  LanguageList,
  LocationList,
  LoadingWrapper,
} from './styled'

const defaultTheme = {
  color: palette.darkGray,
  bgColor: palette.white,
  borderColor: palette.lightGray,
  height: 40,
}

const Wrapper = styled.div(() => ({
  display: 'flex',
  [mediaQueries.tablet]: {
    flexDirection: 'column',
  },
}))

const SelectorWrapper = styled(DefaultLanguageSelectorWrapper)(() => ({
  [mediaQueries.tablet]: {
    width: '100%',
    maxWidth: 'unset',
    marginRight: 0,
    margin: '10px 0 6px 0px',
    '& .country-selector': {
      height: '40px',
    },
    '& .country-wrapper': {
      height: '40px',
    },
  },
}))

const SelectedOption = styled(DefaultSelectedCountry)((props) => ({
  borderRadius: props.opened ? '2px 2px 0 0' : '2px',
  margin: '0 20px 0 0px',
  [mediaQueries.tablet]: {
    margin: '0 0 0 1px',
  },
}))

class LanguageLocationSelector extends Component {
  searchLanguage = debounce(
    (searchTerm) =>
      this.setState({
        filteredLanguages: this.filterLanguages(searchTerm),
      }),
    200,
  )

  searchLocation = debounce(
    (searchTerm) =>
      this.setState({}, () => {
        const { allowCities = false } = this.props
        const { language } = this.state
        if (searchTerm && searchTerm.length > 2 && allowCities) {
          this.getCitySuggestions(searchTerm)
        } else {
          this.setState({
            suggestLocations: [],
            filteredLocations: this.filterLocationsByLanguage(
              [],
              language,
              searchTerm,
            ),
          })
        }
      }),
    700,
  )

  constructor(props) {
    super(props)

    const {
      autoSelect = true,
      enableAllLocations = false,
      allowCities = false,
      useCompetitorLocations = false,
    } = props

    this.langLocOptions = useCompetitorLocations
      ? generateCompetitorLangLocOptions()
      : generateLangLocOptions(
          allowCities,
          enableAllLocations,
          props.supportedLangs,
        )

    let location
    let langCursor = this.langLocOptions.languages.findIndex(
      (item) => item === props.lang,
    )

    const language = langCursor > -1 ? props.lang : 'en'
    const filteredLocations = this.filterLocationsByLanguage([], language)

    if (props.locId) {
      // filteredLocations.supported[0] is  Global Location or US if global is disabled.
      if (isCity(props.locId)) {
        const cachedLocation = getCachedLocationById(
          props.locId,
          props.cachedLocations,
        )
        if (cachedLocation)
          [location] = generateNewLangLocOptions([cachedLocation])
        else props.getLocationsInfo([props.locId])
      } else {
        location =
          filteredLocations.supported.find(
            (c) => String(c.id) === String(props.locId),
          ) || filteredLocations.supported[0]
      }
    } else if (autoSelect) {
      location = getLocationInfo(2840) // use English / United States for Unsupported Countries
    }

    if (
      autoSelect &&
      ((location && props.locId !== location?.id) || props.lang !== language)
    ) {
      props.onChange(location.id, language)
    }

    langCursor = this.langLocOptions.languages.findIndex(
      (item) => item === language,
    )
    const locCursor = this.langLocOptions.locations.findIndex(
      (item) => item.id === location?.id,
    )

    this.state = {
      language,
      location,
      isOpenLanguage: false,
      isOpenLocation: false,
      langCursor,
      locCursor,
      filteredLanguages: this.filterLanguages(null),
      filteredLocations,
      suggestLocations: [],
    }

    this.langInputRef = createRef()
    this.locInputRef = createRef()
  }

  componentDidUpdate(prevProps) {
    const {
      lang,
      locId,
      autoSelect = true,
      useCompetitorLocations = false,
      cachedLocations,
      allowCities = false,
      enableAllLocations = false,
      supportedLangs,
      getLocationsInfo: getLocationsInfoProps,
    } = this.props
    const {
      lang: oldLang,
      locId: oldLocId,
      useCompetitorLocations: oldUseCompetitorLocations,
      cachedLocations: oldCachedLocations,
    } = prevProps
    const { language, location } = this.state

    if (useCompetitorLocations !== oldUseCompetitorLocations) {
      this.langLocOptions = useCompetitorLocations
        ? generateCompetitorLangLocOptions()
        : generateLangLocOptions(
            allowCities,
            enableAllLocations,
            supportedLangs,
          )
    }

    let newLanguage
    let newLocation

    if (isCity(locId)) {
      const oldCachedLocation = getCachedLocationById(locId, oldCachedLocations)
      const cachedLocation = getCachedLocationById(locId, cachedLocations)

      if (!oldCachedLocation && cachedLocation) {
        ;[newLocation] = generateNewLangLocOptions([cachedLocation])
        this.setState({
          location: newLocation,
        })
      }
    }

    if (
      generateKey([lang, locId]) !== generateKey([language, location?.id]) &&
      generateKey([lang, locId]) !== generateKey([oldLang, oldLocId])
    ) {
      newLanguage = lang || 'en'
      const filteredLocations = this.filterLocationsByLanguage([], newLanguage)

      if (locId) {
        if (isCity(locId)) {
          const cachedLocation = getCachedLocationById(locId, cachedLocations)

          if (cachedLocation) {
            ;[newLocation] = generateNewLangLocOptions([cachedLocation])
          } else {
            getLocationsInfoProps([locId])
          }
        } else {
          newLocation =
            filteredLocations.supported.find(
              (c) => String(c.id) === String(locId),
            ) || filteredLocations.supported[0]
        }
      } else if (autoSelect) {
        newLocation = getLocationsInfoProps(2840) // use English / United States for Unsupported Countries
      }

      const langCursor = this.langLocOptions.languages.findIndex(
        (item) => item === language,
      )
      const locCursor = this.langLocOptions.locations.findIndex(
        (item) => item.id === newLocation?.id,
      )
      this.setState({
        language: newLanguage,
        location: newLocation,
        langCursor,
        locCursor,
        filteredLocations,
      })
    }
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  handleClickOutside = (evt) => {
    const { onClose } = this.props
    const { language } = this.state
    this.setState({
      isOpenLanguage: false,
      isOpenLocation: false,
      filteredLanguages: this.filterLanguages(null),
      filteredLocations: this.filterLocationsByLanguage([], language),
      suggestLocations: [],
    })
    this.langInputRef.current.value = ''
    this.locInputRef.current.value = ''
    if (onClose) {
      onClose(evt)
    }
  }

  filterLocationsByLanguage = (
    suggestLocations = [],
    language = undefined,
    search = undefined,
  ) => {
    const { banLocLangs = [], useCompetitorLocations = false } = this.props
    const regExps = (search || '')
      .split(' ')
      .filter((searchWord) => searchWord.trim().length)
      .map((searchWord) => new RegExp(escapeRegExp(searchWord) || '.*.', 'i'))

    const supported = concat(
      filter(
        this.langLocOptions.locations,
        (c) =>
          hasLanguage(c.countryIsoCode, language, useCompetitorLocations) &&
          (useCompetitorLocations
            ? isValidCompetitorLangLoc(language, c.id)
            : true) &&
          !isLanguageBanned(language, banLocLangs[c.id]) &&
          (!search
            ? c.type === 'Country' || c.type === 'All'
            : true &&
              regExps.every((reg) =>
                reg.test(translate(c.canonicalName).split(',')[0]),
              )),
      ),
      filter(
        suggestLocations,
        (c) =>
          hasLanguage(c.countryIsoCode, language, useCompetitorLocations) &&
          !!search,
      ),
    )

    const unSupported = concat(
      filter(
        this.langLocOptions.locations,
        (c) =>
          !(
            hasLanguage(c.countryIsoCode, language, useCompetitorLocations) &&
            (useCompetitorLocations
              ? isValidCompetitorLangLoc(language, c.id)
              : true) &&
            !isLanguageBanned(language, banLocLangs[c.id])
          ) &&
          (!search
            ? c.type === 'Country' || c.type === 'All'
            : true &&
              regExps.every((reg) =>
                reg.test(translate(c.canonicalName).split(',')[0]),
              )),
      ),
      filter(
        suggestLocations,
        (c) =>
          !hasLanguage(c.countryIsoCode, language, useCompetitorLocations) &&
          !!search,
      ),
    )

    return { supported, unSupported }
  }

  filterLanguages = (search) => {
    const regExps = (search || '')
      .split(' ')
      .filter((searchWord) => searchWord.trim().length)
      .map((searchWord) => new RegExp(escapeRegExp(searchWord) || '.*.', 'i'))

    return filter(this.langLocOptions.languages, (c) =>
      regExps.every((reg) => reg.test(translate(c))),
    )
  }

  selectLanguage = (e, language, langCursor) => {
    const { suggestLocations } = this.state
    const filteredLocations = this.filterLocationsByLanguage(
      suggestLocations,
      language,
    )
    let locCursor = 0
    if (this.state.location) {
      locCursor = Math.max(
        filteredLocations.supported.findIndex(
          (c) => c.id === this.state.location.id,
        ),
        0,
      )
    }
    const location = filteredLocations.supported[locCursor]
    this.setState(
      {
        language,
        location,
        filteredLocations,
        isOpenLanguage: false,
        langCursor,
        locCursor,
      },
      () => {
        this.props.onChange(location?.id, language)
        this.locInputRef.current.value = ''
      },
    )

    if (this.props.onClose) {
      this.props.onClose(e)
    }
  }

  debouncedSearchLanguage = (event) => this.searchLanguage(event.target.value)

  selectLocation = (e, location, locCursor) => {
    this.setState(
      {
        location,
        isOpenLocation: false,
        locCursor,
      },
      () => {
        if (isCity(location.id))
          this.props.saveLocationInfo({
            id: location.id,
            is_country: 0,
            name: location.name,
            en: location.en,
            parent_id: location.parentId,
          })
        this.props.onChange(location.id, this.state.language)
      },
    )

    if (this.props.onClose) {
      this.props.onClose(e)
    }
  }

  getCitySuggestions = (searchTerm) => {
    this.props.getLocationSuggestions(searchTerm, (locations) => {
      const suggestLocations =
        locations && locations.length > 0
          ? generateNewLangLocOptions(locations)
          : []

      this.setState((prevState) => ({
        suggestLocations,
        filteredLocations: this.filterLocationsByLanguage(
          suggestLocations,
          prevState.language,
          searchTerm,
        ),
      }))
    })
  }

  debouncedSearchLocation = (event) => this.searchLocation(event.target.value)

  openLanguage = (e) => {
    this.setState(
      (prevState) => ({
        isOpenLanguage: !prevState.isOpenLanguage,
        isOpenLocation: false,
      }),
      () => {
        this.langInputRef.current.focus()
      },
    )

    if (this.props.onOpen && !this.state.isOpenLanguage) {
      this.props.onOpen()
    }
    if (this.props.onClose && this.state.isOpenLanguage) {
      this.props.onClose(e)
    }
  }

  openLocation = (e) => {
    const { isOpenLocation } = this.state

    this.setState(
      {
        isOpenLocation: !isOpenLocation,
        isOpenLanguage: false,
      },
      () => {
        this.locInputRef.current.focus()
      },
    )

    if (this.props.onOpen && !this.state.isOpenLocation) {
      this.props.onOpen()
    }
    if (this.props.onClose && this.state.isOpenLocation) {
      this.props.onClose(e)
    }
  }

  handleLangSearchInputKeyDown = (e) => {
    const { langCursor, filteredLanguages } = this.state

    // On enter, focus main tag input
    if (e.keyCode === 13) {
      e.preventDefault()
      if (filteredLanguages[langCursor])
        this.selectLanguage(e, filteredLanguages[langCursor], langCursor)
      return
    }

    // arrow up/down button should select next/previous list element
    if (e.keyCode === 38 && langCursor > 0) {
      e.preventDefault()
      this.setState((prevState) => ({
        langCursor: prevState.langCursor - 1,
      }))
    } else if (e.keyCode === 40 && langCursor < filteredLanguages.length - 1) {
      e.preventDefault()
      this.setState((prevState) => ({
        langCursor: prevState.langCursor + 1,
      }))
    }
  }

  handleLocSearchInputKeyDown = (e) => {
    const { locCursor, filteredLocations } = this.state

    // On enter, focus main tag input
    if (e.keyCode === 13) {
      e.preventDefault()
      if (filteredLocations.supported[locCursor])
        this.selectLocation(
          e,
          filteredLocations.supported[locCursor],
          locCursor,
        )
      return
    }

    // arrow up/down button should select next/previous list element
    if (e.keyCode === 38 && locCursor > 0) {
      e.preventDefault()
      this.setState((prevState) => ({
        locCursor: prevState.locCursor - 1,
      }))
    } else if (
      e.keyCode === 40 &&
      locCursor < filteredLocations.supported.length - 1
    ) {
      e.preventDefault()
      this.setState((prevState) => ({
        locCursor: prevState.locCursor + 1,
      }))
    }
  }

  getTranslation = (option) => {
    if (isGlobal(option.type)) return translate('all_locations')
    if (option.type === 'Country') return translate(option.name)
    return option.name
  }

  render() {
    const {
      placeholder = 'placeholder_select',
      getSupportedLocationTooltip,
      getUnsupportedLocationTooltip,
      useCompetitorLocations = false,
      isLoadingSuggestions,
      allowCities = false,
      theme,
      style,
      enableAllLocations = false,
      hideLabels = false,
      listHeight = 230,
      isHistoricalSearch,
    } = this.props
    const {
      isOpenLanguage,
      isOpenLocation,
      language,
      location,
      langCursor,
      locCursor,
      filteredLanguages,
      filteredLocations,
      className = '',
    } = this.state
    return (
      <Wrapper style={style} className={className}>
        <SelectorWrapper
          data-testid="language-selector"
          theme={theme || defaultTheme}
          style={{ minWidth: '190px', marginRight: '8px' }}
        >
          {!hideLabels && (
            <Label data-testid="language-selector-label">
              {translate('label_language_dropdown')}
            </Label>
          )}
          <SelectedOption
            data-testid="selected-language-option"
            className={cn('country-selector', {
              '!tw-rounded-md tw-shadow-sm': isHistoricalSearch,
              'tw-rounded-sm tw-shadow-none': !isHistoricalSearch,
            })}
            onClick={this.openLanguage}
            opened={isOpenLanguage}
          >
            <Language name={translate(language || placeholder)} showArrow />
          </SelectedOption>
          <CountryDropdown hide={!isOpenLanguage} className="country-dropdown">
            <SearchInput
              data-testid="language-search-input"
              type="text"
              autoFocus
              placeholder={translate('search_languages')}
              ref={this.langInputRef}
              onChange={this.debouncedSearchLanguage}
              onKeyDown={this.handleLangSearchInputKeyDown}
            />
            <LanguageList
              enableAllLocations={enableAllLocations}
              activeIndex={langCursor}
              options={filteredLanguages}
              getValue={(option) => translate(option)}
              onClick={this.selectLanguage}
              height={listHeight}
            />
          </CountryDropdown>
        </SelectorWrapper>
        <SelectorWrapper
          data-testid="location-selector"
          theme={theme || defaultTheme}
          style={{ minWidth: '250px' }}
        >
          {!hideLabels && (
            <Label data-testid="location-selector-label">
              {translate('label_location_dropdown')}
            </Label>
          )}
          <SelectedOption
            data-testid="selected-location-option"
            className={cn('country-selector', {
              '!tw-rounded-md tw-shadow-sm': isHistoricalSearch,
              'tw-rounded-sm tw-shadow-none': !isHistoricalSearch,
            })}
            onClick={this.openLocation}
            opened={isOpenLocation}
          >
            <Country
              name={translate(
                isGlobal(location?.type)
                  ? 'all_locations'
                  : location?.canonicalName || placeholder,
              )}
              code={location?.countryIsoCode}
              showArrow
            />
          </SelectedOption>
          <CountryDropdown hide={!isOpenLocation} className="country-dropdown">
            <SearchInput
              data-testid="location-search-input"
              type="text"
              autoFocus
              placeholder={translate(
                allowCities ? 'search_cities_countries' : 'search_countries',
              )}
              ref={this.locInputRef}
              onChange={this.debouncedSearchLocation}
              onKeyDown={this.handleLocSearchInputKeyDown}
            />
            {isLoadingSuggestions ? (
              <LoadingWrapper height={230}>
                <LoadingResponsive size="30px" height="48px" />
              </LoadingWrapper>
            ) : (
              <LocationList
                data-testid="location-list"
                activeIndex={locCursor}
                options={filteredLocations}
                getValue={(option) => this.getTranslation(option)}
                language={language}
                onClick={this.selectLocation}
                getSupportedLocationTooltip={getSupportedLocationTooltip}
                getUnsupportedLocationTooltip={getUnsupportedLocationTooltip}
                useCompetitorLocations={useCompetitorLocations}
                height={listHeight}
              />
            )}
          </CountryDropdown>
        </SelectorWrapper>
      </Wrapper>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  isLoadingSuggestions: selectIsLoadingSuggestions(),
  cachedLocations: selectCachedLocations(),
})

const mapDispatchToProps = (dispatch) => ({
  getLocationsInfo: (locIds, onComplete) =>
    dispatch(getLocationsInfo(locIds, onComplete)),
  getLocationSuggestions: (query, onComplete) =>
    dispatch(getLocationSuggestions(query, onComplete)),
  saveLocationInfo: (location) => dispatch(saveLocationInfo(location)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(onClickOutside(LanguageLocationSelector))
